import { getRequestConfig } from "next-intl/server";
import type { RequestConfig, GetRequestConfigParams } from "next-intl/server";

// Define the locales you want to support
export const locales = ["fr", "de", "it", "nl", "es", "pt", "en"] as const; // French first as default
export const defaultLocale = "fr";

/**
 * Each language named in itself, identical in every locale.
 *
 * Deliberately NOT in the message files. A translated language list defeats
 * the only purpose the list has: someone whose interface came up in a language
 * they cannot read is exactly the person who needs to find their own language
 * in it, and "Französisch" is unreadable to the French speaker looking for it.
 * The endonym is the one label that works no matter which locale is active.
 *
 * Keeping these out of `messages/*.json` also stops the drift from returning —
 * a name sitting in de.json is an open invitation for the next translator to
 * helpfully translate it, which is how the list came to say "Französisch" in
 * the first place.
 */
export const localeEndonyms: Record<(typeof locales)[number], string> = {
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  nl: "Nederlands",
  es: "Español",
  pt: "Português",
  en: "English",
};

// Type guard to check if a string is a valid locale from our list
export function isValidLocale(locale: any): locale is (typeof locales)[number] {
  return (locales as readonly string[]).includes(locale);
}

export default getRequestConfig(async (params: GetRequestConfigParams) => {
  const locale = params.locale;

  // For static export, use the provided locale or fall back to default
  let resolvedLocale: (typeof locales)[number];

  if (isValidLocale(locale)) {
    resolvedLocale = locale;
  } else {
    resolvedLocale = defaultLocale;
  }

  try {
    const messages = (await import(`./messages/${resolvedLocale}.json`))
      .default;

    if (!messages) {
      console.error(
        `[i18n.ts] CRITICAL: Messages for locale "${resolvedLocale}" loaded as undefined, null, or empty. This likely indicates a problem with the structure or content of '${resolvedLocale}.json'. Returning empty messages.`
      );
      return { messages: {}, locale: resolvedLocale };
    }

    return { messages, locale: resolvedLocale };
  } catch (error: any) {
    if (error.code === "MODULE_NOT_FOUND") {
      console.error(
        `[i18n.ts] CRITICAL: Message file for locale "${resolvedLocale}" (e.g., '${resolvedLocale}.json') was not found. Returning empty messages. Error:`,
        error
      );
    } else {
      console.error(
        `[i18n.ts] CRITICAL: An unexpected error occurred while loading messages for locale "${resolvedLocale}". Returning empty messages. Error:`,
        error
      );
    }
    return { messages: {}, locale: resolvedLocale };
  }
});
